import { 
  useState, 
  useRef, 
  useEffect, 
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import ChevronDownIcon from './icons/chevrondown.svg?react';
import PlusIcon from './icons/plus.svg?react';
import CheckIcon from './icons/check.svg?react';

/**
 * SimpleDropdown - A reusable dropdown component
 * 
 * @param {Object} props
 * @param {string} props.value - The currently selected value
 * @param {Function} props.onChange - Callback when value changes
 * @param {Array} props.options - Array of {label, value} objects
 * @param {string} props.placeholder - Placeholder text when no value selected
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.width - Width of the dropdown (e.g., 'w-[194px]')
 * @param {boolean} props.searchable - Whether to show search input in dropdown
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {string} props.newOptionText - Text for clickable element for adding new option to list
 * @param {string} props.addNewOption - Function that's called to create new list element
 */
const SimpleDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  width = 'w-auto',
  searchable = false,
  searchPlaceholder = 'Search...',
  newOptionText = '',
  addNewOption = false,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef(null);
  const optionRefs = useRef([]);
  
  const selectedOption = options.find(opt => opt.value === value);
  
  // Filter options based on search query
  const filteredOptions = searchable && searchQuery
    ? options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  // All keyboard-navigable items: options + (optional) "add new"
  const items = useMemo(() => ([
    ...filteredOptions.map(option => ({ type: 'option', option })),
    ...(addNewOption ? [{ type: 'add' }] : []),
  ]), [addNewOption, filteredOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
          setSearchQuery('');
          triggerRef.current?.focus({ preventScroll: true });
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  // Focus search input or options when opening the dropdown
  useEffect(() => {
    if (!isOpen) return;
    if (searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (!searchable && optionsRef.current) {
      optionsRef.current.focus();
    }
  }, [isOpen, searchable]);
  
  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(0);
    }
  }, [isOpen]);
  
  const handleSelect = useCallback((optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
    triggerRef.current?.focus();
  }, [onChange]);
  
  const handleArrowNavigation = useCallback((direction) => {
    setHighlightedIndex(prev => {
      const maxIndex = items.length - 1;
      if (maxIndex < 0) return 0; // no items at all

      if (direction === 'down') {
        return prev < maxIndex ? prev + 1 : prev;
      } else {
        return prev > 0 ? prev - 1 : prev;
      }
    });
  }, [items.length]);

  
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        handleArrowNavigation('down');
        break;
      case 'ArrowUp':
        e.preventDefault();
        handleArrowNavigation('up');
        break;
      case 'Enter':
        e.preventDefault();
        const currentItem = items[highlightedIndex];
        if (!currentItem) return;

        if (currentItem.type === 'option') {
          handleSelect(currentItem.option.value);
        } else if (currentItem.type === 'add') {
          addNewOption?.();
          setIsOpen(false);
          // optionally: triggerRef.current?.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
    }
  }, [items, highlightedIndex, handleArrowNavigation, handleSelect, addNewOption]);

  useEffect(() => {
    if (!isOpen) return;
    const el = optionRefs.current[highlightedIndex];
    el?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, [highlightedIndex, isOpen]);

  return (
    <div className={`relative ${width}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (disabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(prev => !prev);
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) setIsOpen(true)
          }
        }}
        disabled={disabled}
        className={`w-full h-[32px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary focus:border-accent focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between ${
          disabled ? '' : 'hover:border-hover'
        }`}
        {...restProps}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDownIcon 
          className={`size-4 flex-shrink-0 transition-transform text-secondary ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-spacing-1 left-0 right-0 z-50 bg-bg-dropdown border border-border-default rounded shadow-lg overflow-hidden"
          >
            {searchable && (
              <div className="p-spacing-1 border-b border-border-divider">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  data-keybinding-ignore="true"
                  className="w-full h-[28px] border border-border-default rounded px-spacing-2 py-spacing-1 bg-bg-input text-[14px] text-primary placeholder:text-secondary focus:border-accent focus:outline-none transition-colors"
                  autoFocus
                />
              </div>
            )}
            <div 
              ref={optionsRef}
              className="min-h-[32px] max-h-[180px] overflow-y-auto custom-scrollbar"
              style={searchable ? 
                {} : 
                  filteredOptions.length === 0 ? 
                    { 
                      'paddingRight': 'var(--spacing-1)',
                      'paddingLeft': 'var(--spacing-1)',
                      'paddingBottom': 'var(--spacing-1)',
                    } : 
                    { padding: 'var(--spacing-1)' }}
              onKeyDown={handleKeyDown}
              tabIndex={searchable ? -1 : 0}
            >
              {/* Static message when there are no options */}
              {filteredOptions.length === 0 && (
                <div className="px-spacing-2 h-[32px] text-[14px] text-secondary 
                  px-spacing-1 text-left flex flex-no-wrap items-center">
                  No options found
                </div>
              )}

              {items.map((item, index) => {
                const isHighlighted = index === highlightedIndex;

                if (item.type === 'option') {
                  const option = item.option;
                  const isSelected = option.value === value;

                  return (
                    <button
                      type="button"
                      key={option.value}
                      ref={el => { optionRefs.current[index] = el; }}
                      onClick={() => handleSelect(option.value)}
                      className={
                        `w-full h-[32px] text-[14px] px-spacing-3 text-left transition-colors 
                        flex flex-row flex-no-wrap justify-between items-center gap-spacing-2
                        ${
                          isHighlighted ? 'bg-bg-hover text-primary' : 'text-primary hover:bg-bg-hover'
                        } ${
                          index === 0 && 'rounded-t-sm'
                        } ${
                          index === (items.length - 1) && 'rounded-b-sm'
                        }`
                      }
                      tabIndex={-1}
                    >
                      {option.label}
                      {isSelected && <CheckIcon className="size-4 flex-shrink-0 text-accent" />}
                    </button>
                  );
                }

                // item.type === 'add'
                return (
                  <button
                    type="button"
                    key="add-new-option"
                    ref={el => { optionRefs.current[index] = el; }}
                    onClick={() => {
                      addNewOption();
                      setIsOpen(false);
                    }}
                    className={`
                      w-full text-[14px] px-spacing-2 py-spacing-1 
                      text-left transition-colors flex flex-row flex-no-wrap items-center gap-spacing-2
                      border-t border-border-default rounded-b-sm pt-[5px] pb-spacing-1
                      ${
                        isHighlighted
                          ? 'bg-bg-hover text-primary'
                          : 'text-primary hover:bg-bg-hover'
                      }
                    `}
                    tabIndex={-1}
                  >
                    <PlusIcon className="w-[14px] h-[14px]" />
                    <span>{newOptionText ? newOptionText : 'Add new option'}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

SimpleDropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  newOptionText: PropTypes.string,
  addNewOption: PropTypes.func,
};

export default SimpleDropdown;
